/**
 * CookieConsent - DSGVO-konformes Cookie-Consent Banner
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

import { t } from '/js/i18n.js';

export class CookieConsent {
    constructor() {
        this.storageKey = 'boomfection_cookie_consent';
        this.consent = this.loadConsent();
    }

    loadConsent() {
        try {
            const saved = localStorage.getItem(this.storageKey);
            if (saved) return JSON.parse(saved);
        } catch {
            /* expected — localStorage unavailable or consent entry is corrupt JSON */
        }
        return null;
    }

    hasConsent(category) {
        if (!this.consent) return false;
        if (category === 'necessary') return true;
        return this.consent[category] === true;
    }

    hasAnyConsent() {
        return this.consent !== null;
    }

    show() {
        if (this.hasAnyConsent()) return;

        if (!document.getElementById('cookie-consent-style')) {
            const style = document.createElement('style');
            style.id = 'cookie-consent-style';
            style.textContent = `
                .cookie-banner {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background: rgba(15, 10, 30, 0.98);
                    border-top: 1px solid rgba(0, 255, 136, 0.3);
                    padding: 16px 24px;
                    z-index: 100000;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    backdrop-filter: blur(10px);
                    flex-wrap: wrap;
                }
                .cookie-text {
                    color: rgba(255, 255, 255, 0.7);
                    font-size: 0.85rem;
                    line-height: 1.5;
                    flex: 1;
                    min-width: 200px;
                }
                .cookie-text a { color: #00ff88; }
                .cookie-buttons {
                    display: flex;
                    gap: 10px;
                    flex-shrink: 0;
                }
                .cookie-btn {
                    padding: 8px 20px;
                    border-radius: 6px;
                    font-size: 0.85rem;
                    cursor: pointer;
                    border: none;
                    font-weight: 500;
                    transition: opacity 0.2s;
                }
                .cookie-btn:hover { opacity: 0.9; }
                .cookie-btn-accept {
                    background: #00ff88;
                    color: #000;
                }
                .cookie-btn-necessary {
                    background: transparent;
                    color: rgba(255,255,255,0.6);
                    border: 1px solid rgba(255,255,255,0.2);
                }
                @media (max-width: 600px) {
                    .cookie-banner { flex-direction: column; text-align: center; }
                    .cookie-buttons { width: 100%; justify-content: center; }
                }
            `;
            document.head.appendChild(style);
        }

        const banner = document.createElement('div');
        banner.className = 'cookie-banner';
        banner.id = 'cookie-banner';

        const text = document.createElement('div');
        text.className = 'cookie-text';
        text.appendChild(
            document.createTextNode(t('cookie.text'))
        );
        const link = document.createElement('a');
        link.href = '/datenschutz';
        link.textContent = t('cookie.privacyLink');
        text.appendChild(link);

        const buttons = document.createElement('div');
        buttons.className = 'cookie-buttons';

        const btnNecessary = document.createElement('button');
        btnNecessary.className = 'cookie-btn cookie-btn-necessary';
        btnNecessary.textContent = t('cookie.acceptNecessary');
        btnNecessary.addEventListener('click', () => this.accept({ necessary: true, marketing: false }));

        const btnAccept = document.createElement('button');
        btnAccept.className = 'cookie-btn cookie-btn-accept';
        btnAccept.textContent = t('cookie.acceptAll');
        btnAccept.addEventListener('click', () => this.accept({ necessary: true, marketing: true }));

        buttons.appendChild(btnNecessary);
        buttons.appendChild(btnAccept);
        banner.appendChild(text);
        banner.appendChild(buttons);
        document.body.appendChild(banner);
    }

    accept(consent) {
        this.consent = consent;
        localStorage.setItem(this.storageKey, JSON.stringify(consent));
        document.cookie = `cookie_consent=${JSON.stringify(consent)};path=/;max-age=31536000;SameSite=Lax`;

        const banner = document.getElementById('cookie-banner');
        if (banner) banner.remove();

        window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: consent }));
    }
}

export const cookieConsent = new CookieConsent();
